import { createSlice } from '@reduxjs/toolkit';
import {
  AudienceActiveUniqueCountsDto,
  AudienceWithCountsDto,
  CrmAudiencePlatformSupportDto,
  CrmAudienceUploadDto,
  CrmAudienceWithLatestUploadDto,
  LookerReportOutputDto,
} from '@kortxio/hub-api';
import {
  getAllAudiences,
  getAllCrmAudiencePlatformSupport,
  getAllCrmAudienceUploads,
  getAllCrmAudiences,
  getAllReportsForAudience,
  getSelectedCrmAudience,
  runQueryForAudience,
  getAudienceActiveUniqueCountsById,
} from 'features/audiences/async';
import {
  fulfilled,
  initialRequestState,
  pending,
  rejected,
  requestReducer,
  RequestState,
} from 'features/shared/request';

export type AudienceReportQueryResult = {
  name: string;
  data: LookerReportOutputDto | undefined;
  request: RequestState;
};

export type AudiencesState = {
  audiences: {
    data: AudienceWithCountsDto[] | undefined;
    request: RequestState;
  };
  activeUniqueCounts: {
    data: AudienceActiveUniqueCountsDto | undefined;
    request: RequestState;
  };
  crmAudiences: {
    data: CrmAudienceWithLatestUploadDto[] | undefined;
    request: RequestState;
  };
  selectedCrmAudience: {
    data: CrmAudienceWithLatestUploadDto | undefined;
    request: RequestState;
  };
  crmAudienceUploads: {
    data: CrmAudienceUploadDto[] | undefined;
    request: RequestState;
  };
  crmAudiencePlatformSupport: {
    data: CrmAudiencePlatformSupportDto[] | undefined;
    request: RequestState;
  };
  reports: {
    data: LookerReportOutputDto[] | undefined;
    request: RequestState;
  };
  queryResults: AudienceReportQueryResult[] | undefined;
  selectedReport: LookerReportOutputDto | undefined;
};

export const initialState: AudiencesState = {
  audiences: {
    data: undefined,
    request: initialRequestState,
  },
  activeUniqueCounts: {
    data: undefined,
    request: initialRequestState,
  },
  crmAudiences: {
    data: undefined,
    request: initialRequestState,
  },
  selectedCrmAudience: {
    data: undefined,
    request: initialRequestState,
  },
  crmAudienceUploads: {
    data: undefined,
    request: initialRequestState,
  },
  crmAudiencePlatformSupport: {
    data: undefined,
    request: initialRequestState,
  },
  reports: {
    data: undefined,
    request: initialRequestState,
  },
  selectedReport: undefined,
  queryResults: undefined,
};

const slice = createSlice({
  name: 'audiences',
  initialState,
  reducers: {
    setSelectedReport(state, { payload: report }) {
      state.selectedReport = report;
      state.queryResults = undefined;
    },
    resetReports: (state) => {
      state.reports = {
        data: undefined,
        request: initialRequestState,
      };
      state.selectedReport = undefined;
      state.queryResults = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllAudiences.fulfilled, (state, { meta, payload }) => {
      if (state.audiences.request.id === meta.requestId) {
        state.audiences.data = payload;
        state.audiences.request = requestReducer(
          state.audiences.request,
          fulfilled({ meta })
        );
      }
    });
    builder.addCase(getAllAudiences.pending, (state, { meta }) => {
      state.audiences.data = undefined;
      state.audiences.request = requestReducer(
        state.audiences.request,
        pending({ meta })
      );
    });
    builder.addCase(getAllAudiences.rejected, (state, { meta }) => {
      state.audiences.data = undefined;
      state.audiences.request = requestReducer(
        state.audiences.request,
        rejected({ meta })
      );
    });
    builder.addCase(
      getAllCrmAudiences.fulfilled,
      (state, { meta, payload }) => {
        if (state.crmAudiences.request.id === meta.requestId) {
          state.crmAudiences.data = payload;
          state.crmAudiences.request = requestReducer(
            state.crmAudiences.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(getAllCrmAudiences.pending, (state, { meta }) => {
      state.crmAudiences.data = undefined;
      state.crmAudiences.request = requestReducer(
        state.crmAudiences.request,
        pending({ meta })
      );
    });
    builder.addCase(getAllCrmAudiences.rejected, (state, { meta }) => {
      state.crmAudiences.request = requestReducer(
        state.crmAudiences.request,
        rejected({ meta })
      );
    });
    builder.addCase(
      getAllCrmAudiencePlatformSupport.fulfilled,
      (state, { meta, payload }) => {
        if (state.crmAudiencePlatformSupport.request.id === meta.requestId) {
          state.crmAudiencePlatformSupport.data = payload;
          state.crmAudiencePlatformSupport.request = requestReducer(
            state.crmAudiencePlatformSupport.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(
      getAllCrmAudiencePlatformSupport.pending,
      (state, { meta }) => {
        state.crmAudiencePlatformSupport.data = undefined;
        state.crmAudiencePlatformSupport.request = requestReducer(
          state.crmAudiencePlatformSupport.request,
          pending({ meta })
        );
      }
    );
    builder.addCase(
      getAllCrmAudiencePlatformSupport.rejected,
      (state, { meta }) => {
        state.crmAudiencePlatformSupport.request = requestReducer(
          state.crmAudiencePlatformSupport.request,
          rejected({ meta })
        );
      }
    );
    builder.addCase(
      getSelectedCrmAudience.fulfilled,
      (state, { meta, payload }) => {
        if (state.selectedCrmAudience.request.id === meta.requestId) {
          state.selectedCrmAudience.data = payload;
          state.selectedCrmAudience.request = requestReducer(
            state.selectedCrmAudience.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(getSelectedCrmAudience.pending, (state, { meta }) => {
      // A background refresh keeps the rows on screen and shows no loading state.
      if (meta.arg.isRefresh) {
        state.selectedCrmAudience.request.id = meta.requestId;
        return;
      }
      state.selectedCrmAudience.data = undefined;
      state.selectedCrmAudience.request = requestReducer(
        state.selectedCrmAudience.request,
        pending({ meta })
      );
    });
    builder.addCase(getSelectedCrmAudience.rejected, (state, { meta }) => {
      state.selectedCrmAudience.request = requestReducer(
        state.selectedCrmAudience.request,
        rejected({ meta })
      );
    });
    builder.addCase(
      getAllCrmAudienceUploads.fulfilled,
      (state, { meta, payload }) => {
        if (state.crmAudienceUploads.request.id === meta.requestId) {
          state.crmAudienceUploads.data = payload;
          state.crmAudienceUploads.request = requestReducer(
            state.crmAudienceUploads.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(getAllCrmAudienceUploads.pending, (state, { meta }) => {
      // A background refresh keeps the rows on screen and shows no loading state.
      if (meta.arg.isRefresh) {
        state.crmAudienceUploads.request.id = meta.requestId;
        return;
      }
      state.crmAudienceUploads.data = undefined;
      state.crmAudienceUploads.request = requestReducer(
        state.crmAudienceUploads.request,
        pending({ meta })
      );
    });
    builder.addCase(getAllCrmAudienceUploads.rejected, (state, { meta }) => {
      state.crmAudienceUploads.request = requestReducer(
        state.crmAudienceUploads.request,
        rejected({ meta })
      );
    });
    builder.addCase(
      getAudienceActiveUniqueCountsById.fulfilled,
      (state, { meta, payload }) => {
        if (state.activeUniqueCounts.request.id === meta.requestId) {
          state.activeUniqueCounts.data = payload;
          state.activeUniqueCounts.request = requestReducer(
            state.activeUniqueCounts.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(
      getAudienceActiveUniqueCountsById.pending,
      (state, { meta }) => {
        state.activeUniqueCounts.data = undefined;
        state.activeUniqueCounts.request = requestReducer(
          state.activeUniqueCounts.request,
          pending({ meta })
        );
      }
    );
    builder.addCase(
      getAudienceActiveUniqueCountsById.rejected,
      (state, { meta }) => {
        state.activeUniqueCounts.data = undefined;
        state.activeUniqueCounts.request = requestReducer(
          state.activeUniqueCounts.request,
          rejected({ meta })
        );
      }
    );

    // Reporting

    builder.addCase(
      getAllReportsForAudience.fulfilled,
      (state, { meta, payload }) => {
        if (state.reports.request.id === meta.requestId) {
          state.reports.data = payload;
          state.reports.request = requestReducer(
            state.reports.request,
            fulfilled({ meta })
          );
        }
      }
    );
    builder.addCase(getAllReportsForAudience.pending, (state, { meta }) => {
      state.reports.data = undefined;
      state.reports.request = requestReducer(
        state.reports.request,
        pending({ meta })
      );
    });
    builder.addCase(getAllReportsForAudience.rejected, (state, { meta }) => {
      state.reports.data = undefined;
      state.reports.request = requestReducer(
        state.reports.request,
        rejected({ meta })
      );
    });

    builder.addCase(
      runQueryForAudience.fulfilled,
      (state, { meta, payload }) => {
        const { arg } = meta || {};
        const { queryName } = arg || {};

        const queryResults =
          state.queryResults != undefined
            ? state.queryResults.filter((value) => value.name !== queryName)
            : [];

        const queryResultRequestForQueryName = state.queryResults?.find(
          (value) => value?.name === queryName
        )?.request;

        const queryResultsForQueryName = {
          name: queryName,
          data: payload,
          request: requestReducer(
            queryResultRequestForQueryName,
            fulfilled({ meta })
          ),
        };

        state.queryResults = [...queryResults, queryResultsForQueryName];
      }
    );

    builder.addCase(runQueryForAudience.pending, (state, { meta }) => {
      const { arg } = meta || {};
      const { queryName } = arg || {};

      const queryResults =
        state.queryResults != undefined
          ? state.queryResults.filter((value) => value.name !== queryName)
          : [];

      const queryResultRequestForQueryName = state.queryResults?.find(
        (value) => value?.name === queryName
      )?.request;

      const queryResultsForQueryName = {
        name: queryName,
        data: undefined,
        request: requestReducer(
          queryResultRequestForQueryName,
          pending({ meta })
        ),
      };

      state.queryResults = [...queryResults, queryResultsForQueryName];
    });

    builder.addCase(runQueryForAudience.rejected, (state, { meta }) => {
      const { arg } = meta || {};
      const { queryName } = arg || {};

      const queryResults =
        state.queryResults != undefined
          ? state.queryResults.filter((value) => value.name !== queryName)
          : [];

      const queryResultRequestForQueryName = state.queryResults?.find(
        (value) => value?.name === queryName
      )?.request;

      const queryResultsForQueryName = {
        name: queryName,
        data: undefined,
        request: requestReducer(
          queryResultRequestForQueryName,
          rejected({ meta })
        ),
      };

      state.queryResults = [...queryResults, queryResultsForQueryName];
    });
  },
});

const { reducer, actions } = slice;

export const {
  setSelectedReport: setAudienceSelectedReport,
  resetReports: resetAudienceReports,
} = actions;

export default reducer;
